/* global window */
// ============================================================
// PLUMME — AI Regnskapsfører: periodisering
// ============================================================
// Månedsvelgeren var dekorativ: `buildAiAutopilotModel` tok bare imot et år, og
// hver agent filtrerte deretter. Da kunne januar vise et salg fra mars, og
// brukeren hadde ingen måte å jobbe seg gjennom året på.
//
// Her ligger de to tingene som gjør måneden reell:
//  1. `apPeriodIn` — én datosammenligning alle agentene deler.
//  2. `apMonthStatuses` — status for alle tolv månedene. Fargen på en
//     månedsknapp krever at måneden er vurdert, så den kan ikke utledes av den
//     filtrerte oppgavelista for én måned.

// Datoene i UI-modellene er norske (`dd.mm.yyyy`), backend-feltene er ISO.
const apPeriodParse = (value) => {
  if (!value) return null;
  const text = String(value);
  const nb = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(text);
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  const date = nb
    ? new Date(Number(nb[3]), Number(nb[2]) - 1, Number(nb[1]))
    : iso
      ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
      : new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
};

// `month == null` betyr «hele året». Årsagentene bruker den formen, og den er
// også det agentene faller tilbake på før brukeren har valgt noe.
const apPeriodIn = (value, year, month) => {
  const date = apPeriodParse(value);
  if (!date || date.getFullYear() !== Number(year)) return false;
  return month == null || date.getMonth() === Number(month);
};

// En periode kan vaere valgt foer den er moden for sluttkontroller. Agenter som
// trenger en ferdig maaned bruker `open`; arbeid som kan forberedes i innevaerende
// maaned bruker bare `future`.
const apPeriodIsFuture = (year, month, today = new Date()) => month != null && (
  Number(year) > today.getFullYear() ||
  (Number(year) === today.getFullYear() && Number(month) > today.getMonth())
);
const apPeriodIsOpen = (year, month, today = new Date()) => month != null && (
  apPeriodIsFuture(year, month, today) ||
  (Number(year) === today.getFullYear() && Number(month) === today.getMonth())
);

const apPeriodArray = (value) => (Array.isArray(value) ? value : []);

// Overtakelsesdatoen: fra naar Plumme er fasit for regnskapet. Settes av
// regnskapsimporten (`openingBalanceByYear[aar].transitionDate`) og av manuell
// inngaaende balanse. Alt for den er fort i et annet system, og der har ikke AI
// Regnskapsforer kontrollert noe som helst.
const apTransitionDate = (state, year) => {
  const opening = state?.openingBalanceByYear?.[String(year)];
  const date = apPeriodParse(opening?.transitionDate);
  return date && date.getFullYear() === Number(year) ? date : null;
};
// Foerste maaned Plumme selv svarer for. Null naar hele aaret er vaart.
const apTransitionMonth = (state, year) => {
  const date = apTransitionDate(state, year);
  return date ? date.getMonth() : null;
};
// ------------------------------------------------- gjort opp et annet sted
//
// Overtakelsesdatoen daekker bare maaneder INNENFOR ett aar. Et regnskap som
// importeres med aatte aars historikk hadde derfor 2019 staaende som om Plumme
// skulle kontrollert det. To ankre til, som begge er sterkere enn agentenes
// egne funn:
//
//   1. Aaret er levert. `closedYearsByYear` settes av importgodkjenningen naar
//      aarsavslutning, skattemelding og MVA er bekreftet levert — og av
//      historikk-antagelsen for aar langt tilbake. Da er hele aaret en annens.
//   2. Banken er avstemt til en dato. Den datoen er fasit for BANKARBEIDET
//      bakover, ogsaa over aarsskiftet: er driftskontoen avstemt til 30. april
//      2026, er det ingenting aa avstemme i 2019 heller.
const apYearSettled = (state, year) => Boolean(state?.closedYearsByYear?.[String(year)]);

// Kontoene som maa avstemmes. Speiler `settingsBankMustReconcile` i misc.jsx.
const AP_PERIOD_MUST_RECONCILE = ['operating', 'tax_withholding'];
const apPeriodReconcileAccounts = (state) => {
  return apPeriodArray(state?.settings?.bankAccounts)
    .filter((account) => account?.active !== false)
    .filter((account) => AP_PERIOD_MUST_RECONCILE.includes(String(account?.kind || 'operating')) || account?.reconcile === true);
};
// Er EN konto uten dato, gjelder datoen ingenting: maaneden bestaar av alle
// kontoene. Derfor den tidligste datoen, og tom straks en mangler helt.
const apPeriodEarliestDate = (accounts, read) => {
  if (!accounts.length) return '';
  const dates = accounts.map((account) => String(read(account) || ''));
  if (dates.some((date) => !/^\d{4}-\d{2}-\d{2}$/.test(date))) return '';
  return dates.sort()[0];
};

// ------------------------------------------------- to datoer, ikke en
//
// `reconciledThrough` og `startBalanceDate` laa i det samme uttrykket og betydde
// to helt forskjellige ting. Effekten var at et foretak som aldri har importert
// noe fikk «januar er fort i et annet system» paa bank-, avstemmings- og
// bilagsagenten, bare fordi bankkontoens inngaaende saldo var datert i august.
//
//   - `reconciledThrough` settes av regnskapsimporten og av bankoppsettet i
//     overtakelsesmodalen. Den betyr «banken var avstemt til og med denne datoen
//     i det gamle systemet» — altsa en faktisk overtakelse.
//   - `startBalanceDate` er ankeret for saldokjeden: Plumme begynner
//     bankavstemmingen dagen etter. Den sier hvor VAART arbeid starter, og
//     ingenting om hvem som forte maanedene foer.
//
// Begge betyr «her er det ingenting aa avstemme», men bare den forste gir
// Plumme rett til aa si at noen andre gjorde jobben.
const apBankTakeoverThrough = (state) =>
  apPeriodEarliestDate(apPeriodReconcileAccounts(state), (account) => account?.reconciledThrough);
const apBankCoverageFrom = (state) =>
  apPeriodEarliestDate(apPeriodReconcileAccounts(state), (account) => account?.reconciledThrough || account?.startBalanceDate);
// Siste dag i maaneden, som ISO. En maaned er daekket foerst naar hele den er
// det — er banken avstemt til 15. april, er ikke april avstemt.
const apMonthEndIso = (year, month) => {
  const end = new Date(Date.UTC(Number(year), Number(month) + 1, 0));
  return end.toISOString().slice(0, 10);
};

// The opening balance is an end-of-day snapshot, so reconciliation starts on
// the following day. Accounts with incomplete setup remain visible so the
// missing date can be corrected instead of silently hiding the account.
const apBankAccountInMonthScope = (account, year, month) => {
  const source = account?._backend || account || {};
  const followupFrom = String(source.reconciliationFrom || '').slice(0, 10);
  const periodEnd = month == null ? `${Number(year)}-12-31` : apMonthEndIso(year, month);
  if (/^\d{4}-\d{2}-\d{2}$/.test(followupFrom) && !Number.isNaN(Date.parse(followupFrom)) && new Date(followupFrom).toISOString().slice(0,10)===followupFrom && followupFrom > periodEnd) return false;
  const raw = String(source.startBalanceDate || '').slice(0, 10);
  const parsed = new Date(`${raw}T00:00:00Z`);
  const validStart = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    && !Number.isNaN(parsed.getTime())
    && parsed.toISOString().slice(0, 10) === raw;
  if (!validStart) return true;
  return raw < periodEnd;
};

// Maaneden ble gjort opp i et annet system.
const apBankMonthSettled = (state, year, month) => {
  const through = apBankTakeoverThrough(state);
  return Boolean(through) && apMonthEndIso(year, month) <= through;
};
// Maaneden ligger foer Plumme begynte aa avstemme banken. Ingen har gjort jobben
// et annet sted — den er bare utenfor det brukeren har bedt Plumme om aa dekke.
const apBankMonthBeforeCoverage = (state, year, month) => {
  if (apBankMonthSettled(state, year, month)) return false;
  const from = apBankCoverageFrom(state);
  return Boolean(from) && apMonthEndIso(year, month) <= from;
};
// Uansett grunn: her er det ingen bankutskrift aa etterlyse.
const apBankMonthOutOfScope = (state, year, month) =>
  apBankMonthSettled(state, year, month) || apBankMonthBeforeCoverage(state, year, month);

// ------------------------------------------------- aaret er aapnet igjen
//
// `reopen_year` ruller tilbake aarsavslutningen, skattemeldingen og
// aarsregnskapet. Maanedsstatusen utledes av arbeidet som staar igjen, saa et
// gjenaapnet aar trenger ingen egen regel her: er det noe aa gjore i en maaned,
// er den oransje uansett hvem som har vaert innom den for.
const apPeriodNum = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

// NB: de to predikatene under er kopier av `isMileageExpense` og
// bilagsfiltrene i `backend.jsx`. De er duplisert med vilje — hjem-modellen
// leverer bare et årstall for hele året, og månedsstatusen trenger datoen på
// hvert enkelt kjøp. `autopilotPeriods.test.js` sammenligner kildene, slik at
// en endring i backend.jsx ikke kan gli fra hverandre i stillhet.
const apPeriodIsMileage = (expense) =>
  Boolean(expense?.mileageEntryId) ||
  /kj(?:o|ø)rebok|yrkeskj(?:o|ø)ring|kilometergodtgj(?:o|ø)relse/i.test(expense?.description || '');

const apPeriodMissingAttachment = (expense, bankDocumentedIds) =>
  !window.PlummePostingDocumentation?.isBankDocumentedPosting(expense, bankDocumentedIds) &&
  expense.status !== 'draft' &&
  expense.status !== 'annulled' &&
  String(expense.attachmentStatus || '').trim() !== 'missing_lost' &&
  !apPeriodIsMileage(expense) &&
  !expense.attachmentId &&
  !expense.fileName &&
  !expense.receiptFile &&
  !apPeriodArray(expense.attachments).length;

const apPeriodIncompleteTrip = (entry) =>
  !String(entry.purpose || '').trim() ||
  !String(entry.fromLocation || '').trim() ||
  !String(entry.toLocation || '').trim() ||
  apPeriodNum(entry.kilometers) <= 0;

const apPeriodUnsentInvoice = (row) =>
  row.status !== 'betalt' &&
  row.status !== 'paid' &&
  row.paymentStatus !== 'paid' &&
  !row.paidAt &&
  !(Number.isFinite(Number(row.remainingAmount)) && Number(row.remainingAmount) <= 0.005) &&
  row.type === 'faktura' && (
    row.draft || row.status === 'utkast' ||
    (!row.sentAt && row.status !== 'betalt' && row.status !== 'kreditert'));

// Antall åpne punkter per måned, talt fra kildene som faktisk bærer en dato.
// Frister, register og årsoppgjør er med vilje ikke med: de hører ikke til en
// måned, og ville farget tolv knapper på grunn av én ting.
const apOpenItemsByMonth = ({ state, invoiceModel, inboxModel, year }) => {
  const counts = new Array(12).fill(0);
  const ignored = (taskId, itemId, month) => Boolean(state?.autopilotPeriodIgnores?.[`${taskId}:${itemId}@${year}-${String(month + 1).padStart(2, '0')}`]);
  const add = (value) => {
    const date = apPeriodParse(value);
    if (date && date.getFullYear() === Number(year)) counts[date.getMonth()] += 1;
  };
  apPeriodArray(invoiceModel?.rows).filter(apPeriodUnsentInvoice).forEach((row) => {
    const date = apPeriodParse(row.date);
    if (!date || ignored('faktura', row.id, date.getMonth())) return;
    add(row.date);
  });
  const bankDocumentedIds = window.PlummePostingDocumentation?.bankDocumentedPostingIds(state);
  apPeriodArray(state?.expenses).filter((expense) => apPeriodMissingAttachment(expense, bankDocumentedIds)).forEach((expense) => add(expense.date));
  [...apPeriodArray(inboxModel?.ready), ...apPeriodArray(inboxModel?.needsReview)]
    .forEach((entry) => add(entry.date || entry.receiptAnalysis?.receiptDate));
  apPeriodArray(state?.mileageEntries)
    .filter((entry) => entry.status !== 'annulled')
    .filter(apPeriodIncompleteTrip)
    .forEach((entry) => add(entry.date));
  return counts;
};

// MVA-frister som er passert uten at meldingen er levert, plassert i maaneden
// fristen faller i. En glemt frist er ikke det samme som «noe staar igjen» — den
// er en oversittet plikt, og maanedsknappen skal vise det med sin egen farge.
//
// Fristtabellen kommer fra `vatDueDate` i backend.jsx (én kilde, inkludert
// sommerfristen 31. august for 3. termin). Er den ikke tilgjengelig, sier vi
// heller ingenting enn aa gjette paa datoene.
//
// **Fristen kan hoere til fjoraarets termin.** 6. termin (nov-des) forfaller
// 10. februar, og aarlig MVA 10. mars. De ble tidligere hoppet over her — 6.
// termin fordi den «ikke hoerer til noen maaned i dette aarets rutenett», aarlig
// MVA ved en tidlig retur. Men fristen ER i februar og mars, og en glemt
// innlevering skal farge den maaneden den ble glemt i. Begge aarene gaas derfor
// gjennom, og bare frister som lander i DETTE aaret teller.
const AP_VAT_SPAN = { monthly: 1, bimonthly: 2, quarterly: 3, semiannual: 6 };
const apVatTermSubmitted = (state, termYear, termIndex, vatPeriod) => {
  const deadlines = state?.deadlinesByYear?.[String(termYear)] || {};
  if (vatPeriod === 'annual') return Boolean(deadlines.mvaSubmitted);
  return Boolean(deadlines.mvaTerms?.[`term-${termIndex + 1}`]
    || deadlines.mvaTerms?.[`${termYear}-${termIndex + 1}`]);
};
const apMissedVatDeadlines = (state, year, today) => {
  const missed = new Array(12).fill(false);
  if (!state?.settings?.vatRegistered) return missed;
  const dueDate = window.plummeVatDueDate;
  if (typeof dueDate !== 'function') return missed;
  for (const termYear of [Number(year) - 1, Number(year)]) {
    const termVatPeriod=window.PlummeVatPeriodHistory?.forDate?.(state?.settings, `${termYear}-01-01`)||state?.settings?.vatPeriod||'bimonthly';
    const span = AP_VAT_SPAN[termVatPeriod] || 12;
    for (let termIndex = 0; termIndex < Math.ceil(12 / span); termIndex += 1) {
      const due = dueDate(termYear, termIndex, termVatPeriod);
      if (!(due instanceof Date) || Number.isNaN(due.getTime())) continue;
      if (due.getFullYear() !== Number(year) || due >= today) continue;
      if (!apVatTermSubmitted(state, termYear, termIndex, termVatPeriod)) missed[due.getMonth()] = true;
    }
  }
  return missed;
};

// Årsfristene som er passert uten at plikten er gjort opp, plassert i måneden
// fristen faller i — samme regel som `apMissedVatDeadlines` over, men for
// skattemelding, årsregnskap og aksjonærregisteroppgaven.
//
// Kommentaren over `apOpenItemsByMonth` sier at frister og årsoppgjør er utelatt
// med vilje, fordi de «ikke hører til en måned». Det stemmer for ARBEIDET — et
// årsregnskap under arbeid tilhører ikke mars. Det stemmer ikke for FRISTEN:
// den har en dato. Datoene kommer fra den delte katalogen, som også vet at en
// frist i januar 2026 tilhører inntektsåret 2025.
const apMissedAnnualDeadlines = (state, year, today) =>
  globalThis.PlummeAnnualDeadlines?.missedAnnualDeadlinesByMonth?.(state, year, today)
  || new Array(12).fill(false);

// 'done' = grønn, 'review' = oransje, 'overdue' = rød, '' = ingen farge.
//
// En måned med åpne kontrollpunkter skal ha samme varseltone som tallet under
// «Til kontroll». Grønn betyr derfor at alt månedsarbeid faktisk er ferdig.
// Framtidige måneder sier vi ingenting om. En passert måned uten avstemt driftsutskrift er ikke ferdig
// uansett hvor tom den ser ut, men bare når det finnes bankutskrifter i det
// hele tatt: ellers ville et regnskap uten bankimport blitt markert over hele
// linja for noe brukeren ikke har begynt på ennå.
const apMonthStatuses = ({
  state, invoiceModel, inboxModel, reconciledMonths, bankImported, bankControl, year, today = new Date(),
} = {}) => {
  // Maaneder for overtakelsen er verken gronne eller rode. De er en annens:
  // gronn ville pastatt at vi kontrollerte dem, rod at brukeren skylder oss noe.
  const transitionMonth = apTransitionMonth(state, year);
  // Et levert aar er ferdig i sin helhet, uansett hva agentene finner i det.
  const yearSettled = apYearSettled(state, year);
  const open = apOpenItemsByMonth({ state, invoiceModel, inboxModel, year });
  const reconciled = apPeriodArray(reconciledMonths);
  const activeYear = Number(year);
  const currentYear = today.getFullYear();
  const lastPassed = activeYear < currentYear ? 11 : activeYear > currentYear ? -1 : today.getMonth() - 1;
  const missedVat = apMissedVatDeadlines(state, activeYear, today);
  const missedAnnual = apMissedAnnualDeadlines(state, activeYear, today);
  return new Array(12).fill('').map((_, index) => {
    if (bankControl && index <= lastPassed && window.PlummeBankPeriodControl?.monthIssues(bankControl, index)) return 'review';
    if (yearSettled) return 'imported';
    if (transitionMonth != null && index < transitionMonth) return 'imported';
    // En oversittet frist er sterkere enn alt annet: meldingen mangler
    // fortsatt, og da er maaneden ikke gjort opp.
    if (missedVat[index] || missedAnnual[index]) return 'overdue';
    if (activeYear > currentYear || (activeYear === currentYear && index > today.getMonth())) return '';
    const passed = index <= lastPassed;
    // Banken er fasit bakover: er maaneden avstemt for overtakelsen, skal den
    // ikke lyse fordi Plumme mangler en utskrift den aldri skulle hatt.
    const unreconciled = passed && Boolean(bankImported) && !reconciled[index]
      && !apBankMonthOutOfScope(state, year, index);
    const openWork = Boolean(open[index] || unreconciled);
    if (openWork) return 'review';
    // En passert maaned uten aapent arbeid er ferdig. Maanedssignaturen er
    // borte: brukeren skal ikke maatte godkjenne det samme to ganger — forst
    // agent for agent, og saa en gang til paa maaneden — for en signatur ingen
    // regnskapsregel krever. Kommer det noe nytt inn i en gronn maaned, et
    // bilag eller en reversert MVA-termin, gaar den tilbake til oransje av seg
    // selv, fordi fargen leses av arbeidet og ikke av et vedtak.
    //
    // Maaneder for overtakelsen og leverte aar er alt fanget over, saa et
    // importert regnskap blir ikke farget for noe brukeren ikke eier.
    if (passed) return 'done';
    // Maaneden gaar fortsatt: nye linjer kan komme, og gronn ville lovet at den
    // var ferdig.
    return '';
  });
};

Object.assign(window, {
  apPeriodIn,
  apPeriodIsFuture,
  apPeriodIsOpen,
  apMonthStatuses,
  apMissedAnnualDeadlines,
  apOpenItemsByMonth,
  apTransitionDate,
  apTransitionMonth,
  apYearSettled,
  apBankTakeoverThrough,
  apBankCoverageFrom,
  apBankMonthSettled,
  apBankMonthBeforeCoverage,
  apBankMonthOutOfScope,
  apBankAccountInMonthScope,
  apMissedVatDeadlines,
  apPeriodHelpers: { apPeriodParse, apPeriodMissingAttachment, apPeriodIncompleteTrip, apPeriodUnsentInvoice },
});
